import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactDOM from 'react-dom';
import _every from 'lodash/every';
import ContentDrawer from '@gcc/pip-components/dist/ContentDrawer';
import { useHeaderObserver } from '@thd-olt-functional/utils';
import PropTypes from 'prop-types';
import { summaryDrawerOpen } from '../../actions/mobileDrawerActions';
import { summaryDrawerSelector } from '../../selectors/summaryDrawer';
import {
  tablet,
  desktop,
} from '../../utils/constants';
import HeaderContent from './Header';
import FooterContent from './Footer';
import { ContentDrawerWrapper } from './styles';
import ProjectSummary from '../projectSummary';

const SummaryDrawer = ({ canContinue }) => {
  const { isOpen } = useSelector(summaryDrawerSelector);
  const dispatch = useDispatch();
  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  const tabletMediaQueryList = isBrowser
    ? window.matchMedia(`(min-width: ${tablet}) and (max-width: 1023px)`)
    : null;
  const desktopMediaQueryList = isBrowser
    ? window.matchMedia(`(min-width: ${desktop})`)
    : null;
  const [isDesktop, setIsDesktop] = useState(desktopMediaQueryList?.matches || false);
  const [isTablet, setIsTablet] = useState(tabletMediaQueryList?.matches || false);
  const stickyBottomPortalEl = useRef(null);

  /*
  new useHeaderObserver code changes - BEGIN
  */
  const [entry] = useHeaderObserver();
  const bottomLocation = entry?.navigation?.positionBottom && entry?.visible ? entry?.navigation.clientRect?.height : 0;
  const hasHeaderLocation = bottomLocation !== undefined;

  useEffect(() => {
    if (!isBrowser) { return undefined; }

    stickyBottomPortalEl.current = document.createElement('div');
    stickyBottomPortalEl.current.setAttribute('id', 'sticky-bottom-portal-element');
    stickyBottomPortalEl.current.style.bottom = `${bottomLocation}px`;
    stickyBottomPortalEl.current.style.position = 'fixed';
    stickyBottomPortalEl.current.style.transitionProperty = `${bottomLocation}px`;
    stickyBottomPortalEl.current.style.transitionDuration = '500ms';
    stickyBottomPortalEl.current.style.transitionTimingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)';
    stickyBottomPortalEl.current.style.width = '100%';
    stickyBottomPortalEl.current.style.display = 'flex';
    stickyBottomPortalEl.current.style.alignItems = 'center';
    stickyBottomPortalEl.current.style.justifyContent = 'center';
    stickyBottomPortalEl.current.style.zIndex = '950';

    document.body.appendChild(stickyBottomPortalEl.current);
  }, []);

  useEffect(() => {
    if (stickyBottomPortalEl.current !== null) {
      stickyBottomPortalEl.current.style.bottom = `${bottomLocation}px`;
    }
  }, [entry]);
  /*
  new useHeaderObserver code changes - END
  */

  const desktopHandleMediaChange = (e) => {
    setIsDesktop(e.matches);
  };

  const tabletHandleMediaChange = (e) => {
    setIsTablet(e.matches);
  };

  useEffect(() => {
    if (!desktopMediaQueryList || !tabletMediaQueryList) { return undefined; }

    desktopMediaQueryList.addEventListener('change', desktopHandleMediaChange);
    tabletMediaQueryList.addEventListener('change', tabletHandleMediaChange);

    return () => {
      desktopMediaQueryList.removeEventListener('change', desktopHandleMediaChange);
      tabletMediaQueryList.removeEventListener('change', tabletHandleMediaChange);
    };
  }, []);

  const getHeaderHeight = () => {
    if (!isOpen) { return '67px'; }
    if (isTablet) { return '110px'; }
    return '67px';
  };

  const getFooterHeight = () => {
    if (isOpen) { return '120px'; }
    return '81px';
  };

  return (
    (isBrowser && !isDesktop && hasHeaderLocation) && (
      ReactDOM.createPortal(
        (
          <ContentDrawerWrapper data-testid="project-summary-drawer" isOpen={isOpen}>
            <ContentDrawer
              header={<HeaderContent />}
              footer={<FooterContent canContinue={canContinue} />}
              isOpenCallback={(open) => dispatch(summaryDrawerOpen(open))}
              headerHeight={getHeaderHeight()}
              footerHeight={getFooterHeight()}
              open={isOpen}
              lowerNavPosition={bottomLocation}
            >
              <ProjectSummary canContinue={canContinue} isOpen={isOpen} />
            </ContentDrawer>
          </ContentDrawerWrapper>
        ),
        document.getElementById('sticky-bottom-portal-element') || document.body
      )
    )
  );
};

SummaryDrawer.propTypes = {
  bottomBoundary: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canContinue: PropTypes.bool,
};

SummaryDrawer.defaultProps = {
  bottomBoundary: 0,
  canContinue: true,
};

export default SummaryDrawer;
